import {
  $addUpdateTag,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $isRootNode,
  HISTORY_PUSH_TAG,
  SKIP_DOM_SELECTION_TAG,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import {
  ATTACHMENT_NODE_TYPE,
  ATTACHMENT_PAYLOAD_KEY,
  checkAttachmentRef,
  type AttachmentAdapter,
  type AttachmentRef,
} from "../../core/attachments";
import { attachmentLimits, type DocumentLimits } from "../../core/limits";
import type { BlockId, DocumentId } from "../../core/types";
import { $getBlockId } from "../block-id/state";
import type { $placeBlocks } from "../clipboard/extension";
import type { BlockPlace } from "../clipboard/fragment";
import type { AttachmentOutcome, AttachmentRejection } from "./commands";

export interface IntakeConfig {
  documentId: DocumentId;
  /** The latest adapter the host gave, read when files arrive. `null`: attachments are not offered. */
  adapter: { current: AttachmentAdapter | null };
  limits: DocumentLimits;
  /**
   * Media types the host takes, as `accept` on a file input has them: `image/*`, `application/pdf`,
   * `.csv`. Empty takes everything.
   */
  accept: readonly string[];
  /** Told about every file that was offered and is not in the document. */
  reject: { current: (rejection: AttachmentRejection) => void };
  /**
   * What puts content in the document through the paste gate. Handed over rather than imported:
   * it is part of every editor, and this module is not, and the two share as little as they can.
   */
  $place: typeof $placeBlocks;
  /**
   * Where files go instead of the document, in a composer: beside the message. `null` everywhere
   * else. It is told how many files are still being kept, so that a message is not sent without
   * one that was given to it.
   */
  tray: { add(attachment: AttachmentRef): void; setStaging(staging: number): void } | null;
}

export interface IntakeState {
  /** Files handed to the adapter that it has not answered for yet. */
  staging: number;
  /** The last file that was turned away, until it is dismissed or another file goes in. */
  rejection: AttachmentRejection | null;
}

export interface Intake {
  subscribe(listener: () => void): () => void;
  getState(): IntakeState;
  dismiss(): void;
  /** Takes files in at the selection. Must run inside an update or a read: it reads the selection. */
  $add(files: readonly File[]): AttachmentOutcome;
  dispose(): void;
}

const accepts = (file: File, accept: readonly string[]): boolean => {
  if (accept.length === 0) return true;
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return accept.some((pattern) => {
    const wanted = pattern.trim().toLowerCase();
    if (wanted.startsWith(".")) return name.endsWith(wanted);
    if (wanted.endsWith("/*")) return type.startsWith(wanted.slice(0, -1));
    return type === wanted;
  });
};

/** The block at the top level that holds `node`: a table for text in a cell, a list for an item. */
function $topLevelOf(node: LexicalNode): LexicalNode | null {
  let at: LexicalNode = node;
  for (let parent = at.getParent(); parent !== null; parent = parent.getParent()) {
    if ($isRootNode(parent)) return at;
    at = parent;
  }
  return null;
}

/** The block files go after: the one the selection is in, or ends with. `null`: the end. */
function $targetBlockId(): BlockId | null {
  const selection = $getSelection();
  const node = $isRangeSelection(selection)
    ? selection.focus.getNode()
    : $isNodeSelection(selection)
      ? (selection.getNodes().at(-1) ?? null)
      : null;
  const block = node === null ? null : $topLevelOf(node);
  return block === null ? null : $getBlockId(block);
}

const $blockById = (blockId: BlockId): LexicalNode | null =>
  $getRoot()
    .getChildren()
    .find((node) => $getBlockId(node) === blockId) ?? null;

/**
 * Takes files in. Staging is asynchronous and the editor is not held up by it, so everything
 * that was true when a file arrived is checked again when the adapter answers: that this editor
 * is still here and still for writing in, and that the block the file was dropped at still
 * exists. A file never goes into whichever document happens to be open by then: the editor that
 * took it is the only one that can receive it, and a document is never shown by two editors.
 */
export function createIntake(editor: LexicalEditor, config: IntakeConfig): Intake {
  const listeners = new Set<() => void>();
  const gone = new AbortController();
  let state: IntakeState = { staging: 0, rejection: null };
  const set = (next: Partial<IntakeState>): void => {
    state = { ...state, ...next };
    config.tray?.setStaging(state.staging);
    for (const listener of listeners) listener();
  };
  const reject = (rejection: AttachmentRejection): void => {
    set({ rejection });
    config.reject.current(rejection);
  };

  /**
   * Puts a staged file in the document, where `$where` says, and leaves the selection where it
   * is, because whoever is writing has moved on. Answers with the new block's ID.
   */
  const place = (attachment: AttachmentRef, $where: () => BlockPlace | null): BlockId | null => {
    let key: NodeKey | null = null;
    // The file arrives on its own time, and the focus may be anywhere by now: another field on the
    // page, another editor. A DOM selection brings the focus with it, so an editor that does not
    // have it is not given one.
    const root = editor.getRootElement();
    const focused = root !== null && root.contains(root.ownerDocument.activeElement);
    editor.update(
      () => {
        if (!focused) $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        const node = config.$place(
          [{ type: ATTACHMENT_NODE_TYPE, version: 1, [ATTACHMENT_PAYLOAD_KEY]: attachment }],
          $where(),
        );
        key = node?.getKey() ?? null;
      },
      // One file, one undo step: taking back the second of three leaves the other two.
      { discrete: true, tag: HISTORY_PUSH_TAG },
    );
    // Read once the update is over: a new block is given its ID by a transform, at the end of it.
    return editor.getEditorState().read(() => {
      const node = key === null ? null : $getNodeByKey(key);
      return node === null ? null : $getBlockId(node);
    });
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
    dismiss: () => set({ rejection: null }),
    $add(files) {
      const adapter = config.adapter.current;
      if (adapter === null) return { refused: "not-offered", accepted: 0 };
      if (!editor.isEditable()) return { refused: "read-only", accepted: 0 };
      const max = attachmentLimits(config.limits).bytes;
      const taken: File[] = [];
      let refused: AttachmentOutcome["refused"] = null;
      for (const file of files) {
        const reason = !accepts(file, config.accept)
          ? "unsupported-type"
          : file.size > max
            ? "too-large"
            : null;
        if (reason === null) taken.push(file);
        else {
          refused ??= reason;
          reject({
            name: file.name,
            size: file.size,
            reason,
            ...(reason === "too-large" ? { max } : {}),
          });
        }
      }
      if (taken.length === 0) return { refused, accepted: 0 };

      const target = $targetBlockId();
      // Where each file of the batch ended up, so that they stand in the order they were given
      // in whatever order the adapter answers.
      const placed: (BlockId | null)[] = taken.map(() => null);
      set({ staging: state.staging + taken.length, rejection: null });
      taken.forEach((file, index) => {
        const done = (rejection: AttachmentRejection | null): void => {
          set({ staging: state.staging - 1 });
          if (rejection !== null) reject(rejection);
        };
        const about = { name: file.name, size: file.size };
        // A synchronous throw is a failure like a rejection, so the count still comes down and
        // the rest of the batch is still staged.
        Promise.resolve()
          .then(() => adapter.stage(file, { documentId: config.documentId, signal: gone.signal }))
          .then(
            (result) => {
              if (result.status === "refused") {
                return done({
                  ...about,
                  reason: result.reason,
                  ...(result.message === undefined ? {} : { message: result.message }),
                });
              }
              const { attachment } = result;
              const problem = checkAttachmentRef(attachment);
              if (problem !== null) {
                return done({ ...about, reason: "invalid-attachment", message: problem });
              }
              if (gone.signal.aborted || !editor.isEditable()) {
                return done({ ...about, reason: "editor-gone", assetId: attachment.assetId });
              }
              if (config.tray !== null) {
                config.tray.add(attachment);
                return done(null);
              }
              // After the nearest file of the batch before it that is still there; failing that,
              // before the nearest one after it, which got there first; failing that, after the
              // block it was dropped at; failing that, at the end.
              const $first = (ids: readonly (BlockId | null)[]): LexicalNode | null => {
                for (const id of ids) {
                  const node = id === null ? null : $blockById(id);
                  if (node !== null) return node;
                }
                return null;
              };
              placed[index] = place(attachment, () => {
                const previous = $first(placed.slice(0, index).toReversed());
                if (previous !== null) return { after: previous.getKey() };
                const next = $first(placed.slice(index + 1));
                if (next !== null) return { before: next.getKey() };
                const dropped = $first([target]);
                return dropped === null ? null : { after: dropped.getKey() };
              });
              done(
                placed[index] === null
                  ? { ...about, reason: "invalid-attachment", assetId: attachment.assetId }
                  : null,
              );
            },
            (error: unknown) =>
              done({
                ...about,
                reason: "failed",
                message: error instanceof Error ? error.message : String(error),
              }),
          );
      });
      return { refused: null, accepted: taken.length };
    },
    dispose() {
      gone.abort();
      listeners.clear();
    },
  };
}
